/// <amd-module name="VSS/Diagnostics/PerfBar" />
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssComponentProperties, RegionComponent, VssComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { PerformanceConstants } from "VSS/Platform/Performance";
import { getCookie } from "VSS/Platform/Util/Cookie";

import React = require("react");

export interface IVssPerformanceDiagnosticsService extends IVssService {
    setBundlingEnabled(state: boolean): void;

    setDebugEnabled(state: boolean): void;

    setReactStrictModeEnabled(state: boolean): void;

    setDiagnosticsEnabled(state: boolean): void;

    setScriptType(scriptType: string): void;

    setTracePointEnabled(state: boolean): void;

    setCdnEnabled(state: boolean): void;

    setBreakTtiEnabled(state: boolean): void;

    setBreakOnFirstRenderEnabled(state: boolean): void;

    getScriptType(): string;

    isBundlingEnabled(): boolean;

    isDebugEnabled(): boolean;

    isReactStrictModeEnabled(): boolean;

    isDiagnosticsEnabled(): boolean;

    isEsNextSupported(): boolean;

    isTracePointCollectionEnabled(): boolean;

    isCdnAvailable(): boolean;

    isCdnEnabled(): boolean;

    isBreakOnTtiEnabled(): boolean;

    isBreakOnFirstRenderEnabled(): boolean;
}

class VssPerformanceDiagnosticsService extends VssService implements IVssPerformanceDiagnosticsService {
    private static readonly bundlingCookie = "TFS-BUNDLING";
    private static readonly cdnCookie = "TFS-CDN";
    private static readonly debugCookie = "TFS-DEBUG";
    private static readonly diagnosticCookie = "TFS-DIAG";
    private static readonly scriptTypeCookie = "TFS-SCRIPTTYPE";
    private static readonly tracePointCollectorCookie = "TFS-TRACEPOINT-COLLECTOR";

    private breakOnFirstRenderEnabled: boolean;
    private breakOnTtiEnabled: boolean;
    private bundlingEnabled: boolean;
    private cdnEnabled: boolean;
    private debugEnabled: boolean;
    private diagnosticsEnabled: boolean;
    private esNextSupported: boolean;
    private scriptType: string;
    private reactStrictMode: boolean;
    private tracePointCollectionEnabled: boolean;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const pageData = this.pageContext.getService<IVssPageService>("IVssPageService").getData();
        if (pageData) {
            this.bundlingEnabled = pageData.isBundlingEnabled;
            this.cdnEnabled = pageData.isCdnEnabled;
            this.debugEnabled = pageData.isDebugMode;
            this.esNextSupported = pageData.esNextSupported;
            this.diagnosticsEnabled = !!pageData.diagnostics;
            this.scriptType = pageData.scriptType;
            this.tracePointCollectionEnabled = pageData.isTracePointCollectionEnabled;
        }

        let cookieValue = getCookie(PerformanceConstants.BreakOnTtiCookie);
        this.breakOnTtiEnabled = cookieValue === "enabled";

        cookieValue = getCookie(PerformanceConstants.BreakOnFirstRenderCookie);
        this.breakOnFirstRenderEnabled = cookieValue === "enabled";

        cookieValue = getCookie(PerformanceConstants.ReactStrictModeCookie);
        this.reactStrictMode = cookieValue === "enabled";
    }

    public setBundlingEnabled = (state: boolean) => {
        this.bundlingEnabled = state;
        this.setCookieState(VssPerformanceDiagnosticsService.bundlingCookie, state ? "enabled" : "disabled");
    };

    public setDebugEnabled = (state: boolean) => {
        this.debugEnabled = state;
        this.setCookieState(VssPerformanceDiagnosticsService.debugCookie, state ? "enabled" : "disabled");
    };

    public setDiagnosticsEnabled = (state: boolean) => {
        this.diagnosticsEnabled = state;
        this.setCookieState(VssPerformanceDiagnosticsService.diagnosticCookie, state);
    };

    public setScriptType = (scriptType: string) => {
        this.scriptType = scriptType;
        this.setCookieState(VssPerformanceDiagnosticsService.scriptTypeCookie, scriptType);
    };

    public setTracePointEnabled = (state: boolean) => {
        this.tracePointCollectionEnabled = state;
        this.setCookieState(VssPerformanceDiagnosticsService.tracePointCollectorCookie, state ? "enabled" : "disabled");
    };

    public setCdnEnabled = (state: boolean) => {
        this.cdnEnabled = state;
        this.setCookieState(VssPerformanceDiagnosticsService.cdnCookie, state ? "enabled" : "disabled");
    };

    public setBreakTtiEnabled = (state: boolean) => {
        this.breakOnTtiEnabled = state;
        this.setCookieState(PerformanceConstants.BreakOnTtiCookie, state ? "enabled" : "disabled");
    };

    public setBreakOnFirstRenderEnabled = (state: boolean) => {
        this.breakOnFirstRenderEnabled = state;
        this.setCookieState(PerformanceConstants.BreakOnFirstRenderCookie, state ? "enabled" : "disabled");
    };

    public setReactStrictModeEnabled = (state: boolean) => {
        this.reactStrictMode = state;
        this.setCookieState(PerformanceConstants.ReactStrictModeCookie, state ? "enabled" : "disabled");
    };

    public getScriptType(): string {
        return this.scriptType;
    }

    public isBundlingEnabled(): boolean {
        return this.bundlingEnabled;
    }

    public isDebugEnabled(): boolean {
        return this.debugEnabled;
    }

    public isReactStrictModeEnabled(): boolean {
        return this.reactStrictMode;
    }

    public isDiagnosticsEnabled(): boolean {
        return this.diagnosticsEnabled;
    }

    public isEsNextSupported(): boolean {
        return this.esNextSupported;
    }

    public isTracePointCollectionEnabled(): boolean {
        return this.tracePointCollectionEnabled;
    }

    public isCdnAvailable(): boolean {
        const pageData = this.pageContext.getService<IVssPageService>("IVssPageService").getData();
        return !!(pageData && pageData.isCdnAvailable);
    }

    public isCdnEnabled(): boolean {
        return this.cdnEnabled;
    }

    public isBreakOnTtiEnabled(): boolean {
        return this.breakOnTtiEnabled;
    }

    public isBreakOnFirstRenderEnabled(): boolean {
        return this.breakOnFirstRenderEnabled;
    }

    private setCookieState = (name: string, state: boolean | string) => {
        document.cookie = `${name}=${state};path=/`;
    };
}

Services.add("IVssPerformanceDiagnosticsService", { serviceFactory: VssPerformanceDiagnosticsService });

export interface PerfSettingsProps extends IVssComponentProperties {
    onDismiss: () => any;
}

export interface PerfSectionDataSegment {
    segmentLabel: string;
    segmentTooltip?: string;
    segmentContent: string;
}

class PerfBarComponent extends React.Component<{}> {
    public render(): JSX.Element {
        return (
            <div className="performance-panel flex-row flex-center body-s" role="complementary">
                <RegionComponent className="flex-row flex-grow" componentRegion="perfbarSections" />
            </div>
        );
    }
}

VssComponent.register("perfBar", PerfBarComponent);
